@if (_columns) {
  <div
    class="tables"
    [class]="theme"
    [class.scrollable]="widthInPixel"
    [class.sticky-first-column]="widthInPixel && stickyFirstColumn()"
  >
    <table
      [scroll-slaves]="_tableScrollableElement$ | async"
      [sticky]="stickyHeader ? 'top' : null"
      matSort
      class="tables__header"
      #headerRef
    >
      <thead>
        <tr>
          @if (showIndex) {
            <ng-container [ngTemplateOutlet]="indexContainer" />
          }

          @if (_canSelectRows$ | async) {
            <td>
              <div class="line-checkbox">
                <myre-checkbox
                  [ngModel]="_rowSelectionService.allSelected$ | async"
                  [preventDefault]="true"
                  (click)="_rowSelectionService.selectAll(_getRows())"
                />
              </div>
            </td>
          }

          @for (column of _columns; let first = $first; track column.key) {
            <th
              myrePreventDoubleClick
              [mat-sort-header]="column.key"
              [disabled]="!column.canSort"
              [ellipsis]="[
                '.mat-sort-header-container',
                '.mat-sort-header-content',
                '.tables__header-label',
              ]"
              [class.with-header-template]="!!column.headerTemplate"
              [class.first]="first"
              [class.left]="column.align === 'left'"
              [class.center]="column.align === 'center'"
              [class.right]="column.align === 'right'"
              [class.pointer]="column.onClick"
              [ngStyle]="
                widthInPixel
                  ? { 'min-width': column.width }
                  : { flex: column.width }
              "
              [style.max-width]="column.maxWidth"
              [style.text-align]="column.align"
              (onClick)="column.onClick?.($event)"
            >
              @if (column.headerTemplate) {
                <span class="tables__header-label">{{
                  column.label ?? ''
                }}</span>
                <span
                  class="tables__header-template m-margin-l:1"
                  (click)="$event.stopPropagation()"
                  (keydown)="$event.stopPropagation()"
                >
                  <ng-container
                    [ngTemplateOutlet]="column.headerTemplate"
                    [ngTemplateOutletContext]="{ $implicit: column }"
                  />
                </span>
              } @else {
                {{ column.label ?? '' }}
              }
            </th>
          }
        </tr>

        @if (_isBannerVisible()) {
          <tr class="tables__header-banner">
            <td class="tables__header-banner__cell">
              <div class="tables__header-banner__wrapper">
                <div class="tables__header-banner__labels">
                  @if (_areItemsSelected()) {
                    <div class="tables__header-banner__labels-selected">
                      {{ _selectedItems().length | totalElementsTranslate }}
                    </div>
                  }

                  @if (_areItemsSelected() && _bannerTemplate()) {
                    <div class="tables__header-banner__labels-separator">/</div>
                  }

                  @if (_bannerTemplate()) {
                    <div class="tables__header-banner__labels-template">
                      <ng-container
                        *ngTemplateOutlet="_bannerTemplate().template"
                      />
                    </div>
                  }
                </div>

                @if (_areItemsSelected()) {
                  <div class="tables__header-banner__options">
                    <myre-action-group
                      [primaryButtons]="headerActionButton().primaryButtons"
                      [secondaryButtons]="headerActionButton().secondaryButtons"
                      [optionsButtons]="headerActionButton().optionButtons"
                    />
                  </div>
                }
              </div>
            </td>
          </tr>
        }
      </thead>
    </table>

    @if (_dataToDisplay$ | async; as tables) {
      @for (table of tables; track $index) {
        <table
          [scroll-slaves]="_tableScrollableElement$ | async"
          class="table"
          [class.table--hover]="hover"
          #bodyRef
        >
          @if (table.subHeaders; as subHeaders) {
            <thead>
              <tr
                myrePreventDoubleClick
                [class.pointer]="_multiTables"
                (onClick)="_toggleVisibility(table)"
              >
                @if (showIndex) {
                  <ng-container [ngTemplateOutlet]="indexContainer" />
                }

                @if (_canSelectRows$ | async) {
                  <td>
                    <div class="line-checkbox"></div>
                  </td>
                }

                @for (
                  column of _columns;
                  let first = $first;
                  let index = $index;
                  track column.key
                ) {
                  <th
                    ellipsis
                    [class.first]="first"
                    [ngStyle]="
                      widthInPixel
                        ? { 'min-width': column.width }
                        : { flex: column.width }
                    "
                    [style.max-width]="column.maxWidth"
                    [style.text-align]="column.align"
                  >
                    @if (first && table.rows && _multiTables) {
                      <div class="title">
                        <i
                          class="title__icon myre-icon-arrow_dropdown"
                          [class.title__icon--open]="table.isOpen"
                        ></i>
                        <ng-container [ngTemplateOutlet]="content" />
                      </div>
                    } @else {
                      <ng-container [ngTemplateOutlet]="content" />
                    }

                    <ng-template #content>
                      {{ subHeaders[index]?.label ?? '' }}
                    </ng-template>
                  </th>
                }
              </tr>
            </thead>
          }
          @if (table.isOpen) {
            <tbody [class.body__stripe]="stripedRow">
              @for (row of table.rows; let index = $index; track index) {
                <tr
                  myrePreventDoubleClick
                  (onClick)="
                    !table.isCollapseFixed &&
                      row.onClick &&
                      !row.isLoading &&
                      row.onClick($event)
                  "
                  [attr.role]="
                    !table.isCollapseFixed && row.onClick && !row.isLoading
                      ? 'button'
                      : null
                  "
                  [attr.tabindex]="
                    !table.isCollapseFixed && row.onClick && !row.isLoading
                      ? '0'
                      : null
                  "
                  [class.clickable]="
                    !table.isCollapseFixed && row.onClick && !row.isLoading
                  "
                  [class.loading-row]="row.isLoading"
                  [ngClass]="row.isDisabled ? 'm-opacity:0.5' : ''"
                  (keyup.enter)="
                    row.onClick && !row.isLoading && row.onClick($event)
                  "
                >
                  @if (showIndex) {
                    <ng-container
                      [ngTemplateOutlet]="indexContainer"
                      [ngTemplateOutletContext]="{ $implicit: index + 1 }"
                    />
                  }

                  @if (_canSelectRows$ | async) {
                    <ng-container
                      [ngTemplateOutlet]="checkbox"
                      [ngTemplateOutletContext]="{ $implicit: row }"
                    />
                  }

                  @for (
                    column of _columns;
                    let first = $first;
                    track column.key
                  ) {
                    <td
                      [stopPropagation]="
                        !row.isLoading &&
                        (row.cells[column.key]?.type | shouldStopEvent)
                      "
                      [class.title]="first"
                      [attr.title]="row.cells[column.key]?.title || null"
                      [ngStyle]="
                        widthInPixel
                          ? { 'min-width': column.width }
                          : { flex: column.width }
                      "
                      [style.max-width]="column.maxWidth"
                      [class.left]="column.align === 'left'"
                      [class.center]="column.align === 'center'"
                      [class.right]="column.align === 'right'"
                    >
                      @if (row.isLoading) {
                        <myre-line-loader>
                          <span>&nbsp;</span>
                        </myre-line-loader>
                      } @else {
                        @if (row.cells[column.key]; as cell) {
                          @switch (cell.type) {
                            @case (_CellType.String) {
                              <span
                                ellipsis
                                [tooltip]="cell.tooltip"
                                class="cell-string"
                                [class.regular]="
                                  cell.options?.fontWeight === 'regular'
                                "
                                [class.bold]="
                                  cell.options?.fontWeight === 'bold'
                                "
                                [style.text-decoration]="
                                  cell.options?.textDecoration
                                "
                                >{{ cell.value }}</span
                              >
                            }
                            @case (_CellType.Icon) {
                              <i
                                myrePreventDoubleClick
                                [tooltip]="cell.tooltip"
                                [class]="'myre-icon-' + cell.icon"
                                [class.pointer]="cell.onClick"
                                [style.color]="cell.options?.color || 'inherit'"
                                (onClick)="cell.onClick && cell.onClick($event)"
                              ></i>
                            }
                            @case (_CellType.Color) {
                              <div
                                [tooltip]="cell.tooltip"
                                class="cell-color"
                                [style.background-color]="cell.color"
                              ></div>
                            }
                            @case (_CellType.TextWithIconTag) {
                              <myre-text-with-icons-or-tags
                                [elements]="cell.elements"
                                [tooltip]="cell.tooltip"
                                [class.bold]="
                                  cell.valueStyles?.fontWeight === 'bold'
                                "
                                [leftIcon]="cell.valueStyles?.leftIcon"
                                [wrap]="!!cell.valueStyles?.wrap"
                              >
                                {{ cell.value }}
                              </myre-text-with-icons-or-tags>
                            }
                            @case (_CellType.Tag) {
                              <myre-tag
                                [text]="cell.value"
                                [iconClass]="cell.icon"
                                [type]="cell.options?.type ?? _TagType.Primary"
                                [bold]="cell.options?.bold ?? false"
                                [withEllipsis]="true"
                                [tooltip]="cell.tooltip"
                              />
                            }
                            @case (_CellType.DateRange) {
                              <myre-date-range
                                [multiDateRanges]="cell.multiDateRanges"
                                [tooltip]="cell.tooltip"
                              />
                            }
                            @case (_CellType.Button) {
                              <myre-action-button
                                [label]="cell.value"
                                type="primary"
                                color="green"
                                [tooltip]="cell.tooltip"
                                (onClick)="cell.onClick($event)"
                              />
                            }
                            @case (_CellType.ActionsDropdown) {
                              <myre-actions-dropdown
                                [label]="cell.label"
                                [tagType]="cell.tagType"
                                [icon]="cell.icon"
                                [dropdownItems]="cell.dropdownItems"
                                [tooltip]="cell.tooltip"
                              />
                            }
                            @case (_CellType.Form) {
                              <myre-form-cell
                                [element]="cell.element()"
                                (form)="cell.form($event)"
                              />
                            }
                          }
                        }
                      }
                    </td>
                  }
                </tr>
              } @empty {
                <ng-container
                  [ngTemplateOutlet]="empty"
                  [ngTemplateOutletContext]="{ $implicit: table.rows }"
                />
              }
            </tbody>
          }
        </table>
      } @empty {
        <ng-container
          [ngTemplateOutlet]="empty"
          [ngTemplateOutletContext]="{ $implicit: tables }"
        />
      }

      @if (_footer) {
        <div class="footer">
          @switch (_footer.type) {
            @case (_MultiTableType.Actions) {
              <div class="footer__left-buttons">
                @for (
                  leftButton of _footer.leftButtons;
                  track leftButton.label
                ) {
                  <myre-button
                    [label]="leftButton.label"
                    [theme]="_ButtonTheme.Secondary"
                    [isLoading]="leftButton.loading"
                    [icon]="leftButton.icon"
                    (onClick)="leftButton.onClick()"
                  />
                }
              </div>

              <myre-button
                [label]="_footer.rightButton.label"
                [theme]="_ButtonTheme.Primary"
                [isLoading]="_footer.rightButton.loading"
                (onClick)="_footer.rightButton.onClick()"
              />
            }
          }
        </div>
      }
    } @else {
      <ng-container [ngTemplateOutlet]="loading" />
    }
  </div>
} @else {
  <ng-container [ngTemplateOutlet]="loading" />
}

<ng-template #indexContainer let-index>
  <td class="index">{{ index ?? null }}</td>
</ng-template>

<ng-template #loading>
  <myre-box-loader />
</ng-template>

<ng-template #empty let-data>
  @if (data === null || data === undefined) {
    <ng-container [ngTemplateOutlet]="loading" />
  } @else {
    <div class="empty">{{ filterStatus | emptyTranslate }}</div>
  }
</ng-template>

<ng-template #checkbox let-row>
  <td [tooltip]="row.checkboxState.tooltip">
    <myre-checkbox
      [ngModel]="row.checkboxState.checked"
      [readonly]="row.checkboxState.readonly"
      [disabled]="row.checkboxState.disabled"
      [preventDefault]="true"
      (click)="_selectOne(row, $event)"
    />
  </td>
</ng-template>
